import * as React from 'react';
import { motion } from 'framer-motion';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { computeTotals } from '../totals';
import type { CreateInvoiceInput } from '../types';

interface Props {
  form: CreateInvoiceInput;
  clientName?: string;
  clientEmail?: string;
  clientAddress?: string;
  invoiceNumber?: string;
  currency: string;
  /** 036: tax-profile rate override fed into the shared totals math. */
  taxRateOverride?: number | null;
}

/**
 * Client-side WYSIWYG preview that mirrors the server-rendered invoice PDF.
 *
 * Updates instantly as the form changes — no server roundtrip — so the
 * editor feels Stripe Invoicing-class. The real PDF is regenerated on save
 * by the backend (pdf-lib); this preview is "good enough" for typography +
 * line-item layout + totals.
 */
export function InvoiceLivePreview({
  form, clientName, clientEmail, clientAddress, invoiceNumber, currency, taxRateOverride,
}: Props) {
  const { tenant, platform } = useBranding();
  const tenantName = tenant?.company_name ?? platform?.name ?? 'Your Company';
  const tenantAddress = tenant?.company_address ?? '';
  const tenantEmail = tenant?.company_email ?? '';
  const tenantWebsite = tenant?.company_website ?? '';
  const isEstimate = form.document_type === 'estimate';

  // Shared money math — mirrors the server-side totals exactly (036).
  const invoiceDiscount = form.discount_type && Number(form.discount_value) > 0
    ? { type: form.discount_type, value: Number(form.discount_value) }
    : null;
  const totals = computeTotals(form.items, { invoiceDiscount, taxRateOverride });
  const { subtotal, discountTotal, taxTotal, total } = totals;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 110, damping: 22 }}
      className="[color-scheme:light] bg-white text-zinc-950 rounded-2xl ring-1 ring-black/10 shadow-[0_24px_56px_-16px_rgba(0,0,0,0.18)] aspect-[1/1.32] w-full max-w-[640px] mx-auto overflow-hidden flex flex-col"
      style={{ fontFamily: '"Geist Sans", Inter, system-ui, sans-serif' }}
    >
      {/* Saffron top bar — brand accent on the paper */}
      <div className="h-1.5 w-full bg-saffron" aria-hidden />

      <div className="px-6 pt-8 pb-6 sm:px-10 sm:pt-10 sm:pb-8 flex-1 flex flex-col">
        {/* Header — tenant identity on left, INVOICE word on right */}
        <header className="flex items-start justify-between gap-6 mb-8">
          <div>
            <p className="font-semibold text-[20px] tracking-[-0.02em] text-zinc-950">{tenantName}</p>
            {tenantAddress && <p className="text-[11.5px] text-zinc-500 mt-1 max-w-[260px] whitespace-pre-line">{tenantAddress}</p>}
            {tenantEmail && <p className="text-[11.5px] text-zinc-500 mt-0.5">{tenantEmail}</p>}
            {tenantWebsite && <p className="text-[11.5px] text-zinc-500">{tenantWebsite}</p>}
          </div>
          <p className="font-semibold text-[26px] tracking-[0.06em] text-zinc-950 uppercase">{isEstimate ? 'Estimate' : 'Invoice'}</p>
        </header>

        {/* Meta block — invoice number + dates */}
        <section className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <p className="text-[9px] uppercase tracking-[0.16em] font-semibold text-zinc-500">Bill to</p>
            <p className="text-[13.5px] font-semibold text-zinc-950 mt-1.5">{clientName ?? '(pick a client)'}</p>
            {clientEmail && <p className="text-[11.5px] text-zinc-500 mt-0.5">{clientEmail}</p>}
            {clientAddress && <p className="text-[11.5px] text-zinc-500 mt-0.5 whitespace-pre-line">{clientAddress}</p>}
          </div>
          <dl className="text-[11.5px]">
            <dt className="uppercase tracking-[0.16em] text-[9px] font-semibold text-zinc-500">Number</dt>
            <dd className="text-zinc-950 font-mono mt-0.5">{invoiceNumber ?? '(assigned on save)'}</dd>
            <dt className="uppercase tracking-[0.16em] text-[9px] font-semibold text-zinc-500 mt-2">Issued</dt>
            <dd className="text-zinc-950 mt-0.5">{form.issued_at ? formatDate(form.issued_at) : '—'}</dd>
            <dt className="uppercase tracking-[0.16em] text-[9px] font-semibold text-zinc-500 mt-2">Due</dt>
            <dd className="text-zinc-950 mt-0.5">{form.due_at ? formatDate(form.due_at) : '—'}</dd>
          </dl>
        </section>

        {/* Line items */}
        <section className="flex-1">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr className="text-[9px] uppercase tracking-[0.16em] text-zinc-500">
                <th className="text-left py-2 font-semibold border-b border-zinc-200">Description</th>
                <th className="text-right py-2 font-semibold border-b border-zinc-200 w-12">Qty</th>
                <th className="text-right py-2 font-semibold border-b border-zinc-200 w-24">Unit</th>
                <th className="text-right py-2 font-semibold border-b border-zinc-200 w-12">Tax</th>
                <th className="text-right py-2 font-semibold border-b border-zinc-200 w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {form.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-[11.5px] text-zinc-400 italic py-6 text-center">
                    Add line items in the editor on the left.
                  </td>
                </tr>
              )}
              {form.items.map((it, i) => {
                const lineTotal = (totals.lineSubtotals[i] ?? 0) + (totals.lineTaxes[i] ?? 0);
                const disc = it.discount_percent != null && Number(it.discount_percent) > 0 ? Number(it.discount_percent) : null;
                return (
                  <tr key={i}>
                    <td className="py-2 align-top text-[12px] text-zinc-950 border-b border-zinc-100">
                      {it.description || <span className="text-zinc-400 italic">Description…</span>}
                      {disc != null && <span className="text-zinc-400"> (−{disc}%)</span>}
                    </td>
                    <td className="py-2 align-top text-right text-[12px] font-mono text-zinc-950 border-b border-zinc-100">
                      {Number(it.quantity) || 0}
                    </td>
                    <td className="py-2 align-top text-right text-[12px] font-mono text-zinc-950 border-b border-zinc-100">
                      {formatMoney(Number(it.unit_price) || 0, currency)}
                    </td>
                    <td className="py-2 align-top text-right text-[11px] text-zinc-500 font-mono border-b border-zinc-100">
                      {taxRateOverride != null ? Number(taxRateOverride) : (Number(it.tax_rate) || 0)}%
                    </td>
                    <td className="py-2 align-top text-right text-[12px] font-mono font-semibold text-zinc-950 border-b border-zinc-100">
                      {formatMoney(lineTotal, currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex justify-end mt-5">
            <dl className="w-full max-w-[260px] text-[12px] space-y-1">
              <Row label="Subtotal" value={formatMoney(subtotal, currency)} />
              {discountTotal > 0 && (
                <Row
                  label={form.discount_type === 'percent' ? `Discount (${Number(form.discount_value)}%)` : 'Discount'}
                  value={`-${formatMoney(discountTotal, currency)}`}
                />
              )}
              <Row label="Tax" value={formatMoney(taxTotal, currency)} />
              <div className="mt-2 pt-2 border-t border-zinc-200 flex items-baseline justify-between gap-3">
                <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-zinc-500">Total</span>
                <motion.span
                  key={total}
                  initial={{ opacity: 0.4, y: -2 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="font-semibold text-[18px] tabular-nums tracking-[-0.02em] text-zinc-950"
                >
                  {formatMoney(total, currency)}
                </motion.span>
              </div>
            </dl>
          </div>
        </section>

        {/* Notes + Terms */}
        {(form.notes || form.terms) && (
          <section className="mt-8 pt-5 border-t border-zinc-200 grid grid-cols-2 gap-6">
            {form.notes && (
              <div>
                <p className="text-[9px] uppercase tracking-[0.16em] font-semibold text-zinc-500">Notes</p>
                <p className="text-[11px] text-zinc-700 mt-1 whitespace-pre-wrap leading-relaxed">{form.notes}</p>
              </div>
            )}
            {form.terms && (
              <div>
                <p className="text-[9px] uppercase tracking-[0.16em] font-semibold text-zinc-500">Terms</p>
                <p className="text-[11px] text-zinc-700 mt-1 whitespace-pre-wrap leading-relaxed">{form.terms}</p>
              </div>
            )}
          </section>
        )}

        <footer className="mt-auto pt-6 text-[9px] uppercase tracking-[0.18em] font-semibold text-zinc-400 text-center">
          Worqly · paper-class preview
        </footer>
      </div>
    </motion.div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-zinc-500">{label}</span>
      <span className="font-mono tabular-nums text-zinc-950">{value}</span>
    </div>
  );
}
